import * as React from 'react';
import ReactDOM from 'react-dom';
import useSheet from 'app/jss';
import classNames from 'classnames';
import t from 'app/utils/translate';
import { connect } from 'react-redux';
import { partition, debounce, sortBy, some } from 'lodash';
import { withRouter } from 'react-router-dom';
import { TimelineMax, TweenMax, Circ } from 'gsap';
import { FRIEND_GROUP_ID, NEW_GROUP_ID } from 'app/constants/friendConstants';
import { createFriendRequests } from 'app/actions/friendRequestActions';
import { saveGroupName, deleteGroup } from 'app/actions/groupActions';
import { FRIEND, YOU_REQUESTED } from 'app/constants/friendStatusTypes';
import { searchUsers, clearResults } from 'app/actions/friendSearchActions';
import { viewFriend } from 'app/actions/friendsActions';
import manageFriendsSelector from 'app/selectors/friends/manageFriendsSelector';
import * as Enter from 'app/utils/animations/enter';
import * as Leave from 'app/utils/animations/leave';
import GroupContainer from 'app/components/friends/GroupContainer';
import FriendsCardList from 'app/components/friends/FriendsCardList';
import FriendCard from 'app/components/friends/friendCard/FriendCard';
import friendsInGroupSelector from 'app/selectors/friends/friendsInGroupSelector';
import FriendInfoMessage from 'app/components/friends/common/FriendInfoMessage';
import FriendInfoMessageItem from 'app/components/friends/common/FriendInfoMessageItem';
import GroupContainerCategories from 'app/components/friends/GroupContainerCategories';
import GroupContainerCategoryItem from 'app/components/friends/GroupContainerCategoryItem';
import Icon from 'app/components/commons/Icon';
import GroupEditableTitle from 'app/components/friends/groups/GroupEditableTitle';
import GroupDeleteConfirm from 'app/components/friends/groups/GroupDeleteConfirm';
import GroupContainerHeader from 'app/components/friends/GroupContainerHeader';
import GroupContainerSearch from 'app/components/friends/GroupContainerSearch';
import FriendsRowList from 'app/components/friends/FriendsRowList';
import Tappable from 'app/components/commons/Tappable';
import FriendSlider from 'app/components/friends/common/FriendSlider';
import GroupContentsSearchResults from 'app/components/friends/GroupContentsSearchResults';
import BouncyLoader from 'app/components/commons/BouncyLoader';
import AnimatedGroup from 'app/components/commons/AnimatedGroup';
import AnimatedGroupChild from 'app/components/commons/AnimatedGroupChild';
import FriendRowExistingGroup from 'app/components/friends/FriendRowExistingGroup';

type ProvidedProps = {
  onClearSearchResults: (searchId: number | string) => void,
  onSearchUsers: (term: string) => void,
  onTitleSave: (groupId: number, title: string) => void,
  onRemoveGroup: (groupId: number) => void,
  onViewFriend: (id: string | number) => void,
  groupMembers?: Array<Friend | FriendRequest>,
  manageableFriends?: Array<iFriendUser>,
  history: Object,
  isSaving?: boolean,
  isRemoving?: boolean,
  className?: string,
  anyFriends?: boolean,
  anyRequests?: boolean,
  onCreateFriendRequest: () => void,
  groups: object,
};

type RealProps = {
  classes: object,
  group: FriendGroup,
  // not implemented at the moment...
  isMobile?: boolean,
};

type Props = RealProps & ProvidedProps;

type State = {
  selectedUser: iFriendUser,
  activeCategory: Object,
  searchTerm: string,
  isSearching: boolean,
  isDeleting: boolean,
  title: string,
};

const categories = {
  friends: {
    id: 1,
    name: t('Members'),
    icon: 'icon-group',
    isDisabled: () => false,
  },
  pending: {
    id: 2,
    name: t('Pending'),
    icon: 'icon-pending',
    isDisabled: (props: Props) => !props.groupMembers.some(member => member.status === YOU_REQUESTED),
  },
  edit: {
    id: 3,
    name: t('Manage'),
    icon: 'icon-edit',
    align: 'right',
    isDisabled: (props: Props) => !props.anyFriends && props.groupMembers.length === 0,
  },
};

class GroupContentsExisting extends React.Component {
  props: Props;
  state: State = {
    activeCategory: this.props.group.id === NEW_GROUP_ID ? categories.edit : categories.friends,
    searchTerm: '',
    title: this.props.group.name,
  };

  static defaultProps = {
    manageableFriends: [],
    groupFriends: [],
    isMobile: true,
    isSaving: false,
    isRemoving: false,
    className: undefined,
    anyFriends: false,
    anyRequests: false,
  };

  setCategory = (category) => {
    this.setState({ activeCategory: category });
  }

  resetEditMode = () => {
    this.setState({ activeCategory: categories.friends });
  }

  toggleUser = (user: iFriendUser) => {
    const { selectedUser } = this.state;
    this.props.onViewFriend(user.id);

    if (selectedUser && selectedUser.id === user.id) {
      this.setState({ selectedUser: undefined });
    } else {
      this.setState({ selectedUser: user });
    }
  }

  triggerSearch = debounce((term) => {
    this.props.onSearchUsers(term, this.props.group.id);
    this.setState({ searchTerm: term });
  }, this.props.isMobile ? 700 : 400)

  onSearchChange = (terms: string) => {
    if (terms) {
      this.triggerSearch(terms);
    } else {
      // if they cleared it, set it straitht away
      this.setState({ searchTerm: terms });
      this.props.onClearSearchResults(this.props.group.id);
    }
  }

  onSearchStart = () => {
    this.setState({ isSearching: true });
  }

  onSearchEnd = () => {
    this.setState({ isSearching: false });
  }

  saveTitle = (newTitle, isInvalid) => {
    const {
      group,
      onTitleSave,
    } = this.props;

    if (!isInvalid && newTitle && newTitle !== group.name) {
      onTitleSave(group.id, newTitle);
    }
  }

  onCheckRemove = () => {
    this.setState({ isDeleting: true });
  }

  onCancelRemove = () => {
    this.setState({ isDeleting: false });
  }

  onConfirmRemove = () => {
    this.props.onRemoveGroup(this.props.group.id);
  }

  onShrink = () => {
    this.props.history.push(`/groups/${FRIEND_GROUP_ID}`);
  }

  setContainerRef = (node) => {
    this.containerRef = node;
  }

  onMessageClick = (item) => {
    if (item.key === 1) {
      this.setState({ isSearching: true });
    } else if (item.key === 2) {
      this.setState({ activeCategory: categories.edit });
    }
  }

  getDeleteGrow = () => {
    if (!this.deleteGrow) {
      this.deleteGrow = (node, context) => {
        const childContainer = node.firstElementChild;
        const groupContainer = ReactDOM.findDOMNode(this.containerRef);
        const { height, width } = window.getComputedStyle(groupContainer);
        TweenMax.set(childContainer, { height, width });

        const radius = Math.hypot(parseInt(height, 10), parseInt(width, 10));

        const timeline = new TimelineMax();
        timeline.add(Enter.growCircular(0.5)(node, context), 0);
        timeline.add(TweenMax.fromTo(childContainer, 0.5, { x: 0, y: 0 },
        { x: radius, y: radius, ease: Circ.easeOut, clearProps: 'x,y' }), 0);

        return timeline;
      };
    }
    return this.deleteGrow;
  }

  getDeleteShrink = () => {
    if (!this.deleteShrink) {
      this.deleteShrink = (node) => {
        const childContainer = node.firstElementChild;
        const groupContainer = ReactDOM.findDOMNode(this.containerRef);
        const { height, width } = window.getComputedStyle(groupContainer);
        TweenMax.set(childContainer, { height, width });

        const radius = Math.hypot(parseInt(height, 10), parseInt(width, 10));
        const timeline = new TimelineMax();
        timeline.add(Leave.shrinkCircular(0.5)(node), 0);
        timeline.add(TweenMax.fromTo(childContainer, 0.5, { x: radius, y: radius },
        { x: 0, y: 0, ease: Circ.easeOut }), 0);

        return timeline;
      };
    }
    return this.deleteShrink;
  }

  buildCategoryItem = (category) => {
    const { activeCategory } = this.state;

    const isActive = activeCategory === category;
    const isDisabled = category.isDisabled(this.props);

    return (
      <GroupContainerCategoryItem
        isActive={isActive}
        disabled={isDisabled}
        onClick={() => this.setCategory(category)}
        key={category.id}
      >
        <Icon type={category.icon} />
        <span>{category.name}</span>
      </GroupContainerCategoryItem>
    );
  }

  buildCategories = () => {
    const { classes } = this.props;

    const [rightAligned, leftAligned] = partition(categories, 'align');

    return [
      (<div className={classes.itemsLeft} key="left">
        {leftAligned.map(this.buildCategoryItem)}
      </div>),
      (<div className={classes.itemsRight} key="right">
        {rightAligned.map(this.buildCategoryItem)}
      </div>),
    ];
  }

  buildCards = (userList: Array<iFriendUser>) => userList.map(user => (
    <FriendCard
      friend={user}
      onClick={() => this.toggleUser(user)}
      key={user.id}
    />
  ))

  buildInfoMessages = () => {
    const {
        anyFriends,
        groupMembers,
      } = this.props;

    const messages = [];
    if (!anyFriends) {
      messages.push({
        key: 1,
        icon: 'icon-search',
        text: t('find friends from search'),
        color: 'primary',
      });
    } else if (!groupMembers.some(member => member.status === FRIEND)) {
      messages.push({
        key: 2,
        icon: 'icon-add-friend',
        text: t('add friends'),
        color: 'primary',
      });
    }

    if (messages.length > 0) {
      return (
        <FriendInfoMessage key="add-friends">
          {messages.map(message => (<FriendInfoMessageItem
            {...message}
            onClick={() => this.onMessageClick(message)}
          />))}
        </FriendInfoMessage>
      );
    }
    return undefined;
  }

  render() {
    const {
      classes,
      group,
      groupMembers,
      isSaving,
      manageableFriends,
      isRemoving,
      className,
      onCreateFriendRequest,
      groups,
    } = this.props;
    const {
      activeCategory,
      isDeleting,
      searchTerm,
      selectedUser,
      isSearching,
    } = this.state;

    const editMode = activeCategory === categories.edit;

    let userList = [];
    switch (activeCategory) {
      case categories.friends:
        userList = groupMembers.filter(m => m.status === FRIEND);
        break;
      case categories.pending:
        userList = groupMembers.filter(m => m.status !== FRIEND);
        break;
    }

    return (
      <AnimatedGroup className={classNames(classes.groupContents, className)}>
        <FriendSlider
          friend={selectedUser}
          onBack={() => this.toggleUser(selectedUser)}
        />
        {isDeleting &&
          <AnimatedGroupChild
            onEnter={this.getDeleteGrow()}
            onLeave={this.getDeleteShrink()}
            key="deleteconfirm"
          >
            <GroupDeleteConfirm
              group={group}
              onConfirmRemove={this.onConfirmRemove}
              onCancelRemove={this.onCancelRemove}
              removing={isRemoving}
            />
          </AnimatedGroupChild>
        }
        <GroupContainer className={classes.rounded} ref={this.setContainerRef}>
          <GroupContainerHeader>
            <GroupEditableTitle
              title={group.name}
              onTitleSave={this.saveTitle}
              onTitleChange={this.onTitleChange}
              onRemove={this.onCheckRemove}
              onShrink={this.onShrink}
              groupId={group.id}
              invalidConfig={[
                {
                  id: 'required',
                  isInvalid: newTitle => !newTitle,
                  reason: t('Group needs a name'),
                },
                {
                  id: 'groupexists',
                  isInvalid: newTitle => some(groups, g => g.id !== group.id && g.name === newTitle),
                  reason: t('Group name already exists'),
                }]}
            />
            <AnimatedGroup
              className={classes.searchRow}
              onEnter={Enter.appear(0.3, 0.3)}
              onLeave={Leave.disappear(0.3)}
            >
              <GroupContainerSearch
                className={classes.searchBar}
                isSearching={isSearching}
                onSearchChange={this.onSearchChange}
                onSearchStart={this.onSearchStart}
                onSearchEnd={this.onSearchEnd}
              />
              {editMode && !isSearching &&
                <Tappable
                  className={classes.doneButton}
                  onTap={this.resetEditMode}
                  key="done"
                >
                  <Icon type="icon-tick" size={9} />
                  <span>{t('Done')}</span>
                </Tappable>
              }
              {isSaving &&
                <div className={classes.groupSavingLoader} key="saving">
                  <BouncyLoader scheme="dark" />
                </div>
              }
            </AnimatedGroup>
          </GroupContainerHeader>
          {!editMode && !searchTerm &&
            <AnimatedGroup
              className={classNames(classes.innerContainer, { isSearching: !!searchTerm })}
              onEnter={Enter.appear(0.3, 0.3)}
              onLeave={Leave.disappear()}
            >
              <GroupContainerCategories>
                {this.buildCategories()}
              </GroupContainerCategories>
              {activeCategory === categories.friends && this.buildInfoMessages()}
              {userList.length > 0 &&
                <FriendsCardList shortWidth>
                  {this.buildCards(userList)}
                </FriendsCardList>
              }
            </AnimatedGroup>
          }
          {!searchTerm && editMode && (
            <FriendsRowList>
              {sortBy(manageableFriends, 'username').map(user => (
                <FriendRowExistingGroup
                  key={user.id}
                  friend={user}
                  group={group}
                  members={group.members}
                  onRowClick={() => this.toggleUser(user)}
                />
              ))}
            </FriendsRowList>
          )}
          {searchTerm && (
            <GroupContentsSearchResults
              group={group}
              searchId={group.id}
              searchTerm={searchTerm}
              onCreateFriendRequest={onCreateFriendRequest}
              members={group.members}
            />
          )}
        </GroupContainer>
      </AnimatedGroup>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    groupContents: {
      position: 'relative',
      display: 'flex',
      overflow: 'hidden',
      height: '100%',
      borderRadius: [8, 8, 0, 0],
      // kick the element into GPU rendering mode
      // this prevents background bleed problems when a parent is being translated
      // such as in the desktop carousel
      transform: 'translateZ(0px)',
      [theme.mediaQuery.tabletPortraitAndBelow]: {
        flex: [1, 0, 'calc(100% - 20px)'],
        height: 'auto',
      },
    },
    rounded: {
      flexGrow: 1,
    },
    itemsLeft: {
      display: 'flex',
    },
    groupSavingLoader: {
      width: 40,
    },
    itemsRight: {
      display: 'flex',
      flexGrow: 1,
      justifyContent: 'flex-end',
    },
    innerContainer: {
      padding: [15, 10],
      '&.isSearching': {
        padding: 0,
        height: '100%',
      },
      '& > *:not(:last-child)': {
        marginBottom: 15,
      },
    },
    doneButton: {
      display: 'flex',
      alignItems: 'center',
      color: theme.color.primary,
      transition: 'color 0.2s, opacity 0.2s',
      opactiy: 1,
      fontSize: 13,
      fontWeight: 600,
      '& >*:not(:last-child)': {
        marginRight: 2,
      },
      '&.disabled': {
        color: theme.color.basis4,
        opacity: 0.35,
      },
    },
    searchRow: {
      display: 'flex',
    },
    searchBar: {
      flexGrow: 1,
    },
  };
}

function mapStateToProps(state: GlobalState, props: Props) {
  return {
    anyFriends: some(state.friends.friends),
    anyRequests: some(state.friendRequests.friendRequests),
    manageableFriends: manageFriendsSelector(state, props.group.id),
    groupMembers: friendsInGroupSelector(state, props.group.id),
    isSaving: state.friendGroups.savingGroups.has(props.group.id),
    isRemoving: state.friendGroups.deletingGroups.has(props.group.id),
    groups: state.friendGroups.groups,
  };
}

const actions = {
  onCreateFriendRequest: createFriendRequests,
  onTitleSave: saveGroupName,
  onRemoveGroup: deleteGroup,
  onSearchUsers: searchUsers,
  onViewFriend: viewFriend,
  onClearSearchResults: clearResults,
};

export default connect(mapStateToProps, actions)(
  withRouter(useSheet(applyStyles)(GroupContentsExisting)),
);
